import { z } from 'zod';

import { dateTimeSchema } from '@apple/utils/globalization/models';

import type { Product } from '@apple/features/product/models/management';
import { manzanitaProductSchema } from '@apple/features/product/models/management';
import { type ProductKey, productKeySchema } from '@apple/features/product/models/product';

import type {
	ProgramAssociation,
	SubProgramAssociation,
	UserRoleAssociation,
} from './associations';
import {
	programAssociationSchema,
	subProgramAssociationSchema,
	userRoleAssociationSchema,
} from './associations';

/**
 * @see Manzanita.Data.Manzanita.ManzanitaProduct
 * @see AppleBrandedPrograms.Catalog.AppleBrandedProduct
 */
export interface AppleBrandedProduct extends Product {
	quantityLimit: number | null;
	customerGroupAssociations: ProgramAssociation[];
	customerGroupCountryAssociations: SubProgramAssociation[];
	quantityLimits: UserRoleProductQuantityLimit[];
	userRoleAssociations: UserRoleAssociation[];
}

/** AppleBrandedPrograms.Catalog.QuantityLimits.UserRoleProductQuantityLimit */
export interface UserRoleProductQuantityLimit {
	itemId: ProductKey;
	roleId: number;
	maxLimit: number;
	startDate: Date | null;
	endDate: Date | null;
	updatedDate?: Date | null;
	isDeleted?: boolean;
}

export const userRoleProductQuantityLimitSchema = z.object({
	itemId: productKeySchema,
	roleId: z.number(),
	maxLimit: z.number(),
	startDate: dateTimeSchema.nullable(),
	endDate: dateTimeSchema.nullable(),
	updatedDate: dateTimeSchema.nullable().optional(),
	isDeleted: z.boolean().optional(),
}) as z.ZodType<UserRoleProductQuantityLimit>;

export const appleBrandedProductSchema = manzanitaProductSchema.and(
	z.object({
		quantityLimit: z.coerce.number().nullable(),
		customerGroupAssociations: z.array(programAssociationSchema),
		customerGroupCountryAssociations: z.array(subProgramAssociationSchema),
		quantityLimits: z.array(userRoleProductQuantityLimitSchema),
		userRoleAssociations: z.array(userRoleAssociationSchema),
	}),
) satisfies z.ZodType<AppleBrandedProduct>;

export const DEFAULT_PRODUCT = {
	itemId: '',
	sku: '',
	title: '',
	description: '',
	unitOfMeasure: '',
	categoryMappings: [],
	keywords: [],
	imagesJson: '[]',
	beginDate: null,
	endDate: null,
	externalUrl: '',
	quantityLimit: null,
	requiresApproval: false,
	isBackorderable: false,
	userRoleAssociations: [],
	comments: '',

	allAssociation: null,
	plantAssociations: [],
	countryAssociations: [],
	customerGroupAssociations: [],
	customerGroupCountryAssociations: [],

	newProductIntroductionRecords: [],
	endOfLifeRecords: [],
	priceOverrides: [],
	quantityLimits: [],
	localizations: [],

	showInventory: false,
	showInventoryStatus: false,
} as const satisfies AppleBrandedProduct;
